import { FeatureGrantScopeType, FeatureState } from '../../../graphql/types';

// Mirrors SCOPE_PRECEDENCE in the backend's FeatureAccessRepository: a grant is
// redundant when whatever would decide the feature after it is deleted already
// decides it the same way. Replayable here because the engine resolves a
// member's scope ids from data the admin API also exposes — location = their
// office location, studio = that location's studio, country = their country
// code, client = their fit client.

export interface CleanupGrant {
  id: string;
  clientId: string;
  featureId: string;
  featureKey: string;
  scopeType: FeatureGrantScopeType;
  scopeId: string;
  state: FeatureState;
}

export interface CleanupFeature {
  id: string;
  key: string;
  defaultState: FeatureState;
  isArchived: boolean;
}

export interface CleanupUser {
  id: string;
  email: string;
  clientId: string | null;
  countryCode: string | null;
  locationId: string | null;
  studioId: string | null;
}

export interface CleanupLocation {
  id: string;
  name: string | null;
  studioId: string | null;
  clientId: string | null;
}

export interface CleanupStudio {
  id: string;
  name: string | null;
  clientId: string | null;
}

export type CleanupVerdict =
  /** The rest of the chain already resolves to this grant's state. */
  | 'redundant'
  /** Can't be decided from the admin API alone — a human has to look. */
  | 'review'
  /** The grant changes the outcome; leave it alone. */
  | 'keep';

export interface GrantAnalysis {
  grant: CleanupGrant;
  verdict: CleanupVerdict;
  /** State the feature resolves to once this grant is deleted, when knowable. */
  fallbackState: FeatureState | null;
  /** Human label for what decides it instead ("client grant", "studio grant"). */
  fallbackSource: string | null;
  /** The grant that decides it, when one does rather than the feature default. */
  fallbackGrantId: string | null;
  reason: string;
  /** Email / name / country code of the grant's scope target. */
  targetLabel: string;
}

export interface AnalyzeFeatureGrantsInput {
  grants: CleanupGrant[];
  /** Name of the client every grant belongs to — the client scope's target. */
  clientName: string;
  features: Map<string, CleanupFeature>;
  users: Map<string, CleanupUser>;
  locations: Map<string, CleanupLocation>;
  studios: Map<string, CleanupStudio>;
}

/** What decides the feature once a grant is gone. Null state = can't be told. */
interface Fallback {
  state: FeatureState | null;
  source: string;
  grantId?: string;
}

const FEATURE_DEFAULT = 'feature default';

const scopeGrantSource = (scopeType: FeatureGrantScopeType) =>
  `${scopeType} grant`;

// The engine's precedence order, most specific first.
const CHAIN = [
  FeatureGrantScopeType.User,
  FeatureGrantScopeType.Location,
  FeatureGrantScopeType.Studio,
  FeatureGrantScopeType.Country,
  FeatureGrantScopeType.Client,
];

/** A scope whose id differs between the members under the grant being judged. */
const PER_MEMBER = Symbol('per-member');

/**
 * Which scope id applies at each level for the members a grant covers. Missing
 * means the level doesn't resolve for them and the engine skips it.
 */
type ScopeTargets = Partial<
  Record<FeatureGrantScopeType, string | typeof PER_MEMBER>
>;

/** Why a grant can't be judged: its target didn't load, or it never fires. */
type Problem = { unloadable: string } | { inert: string };

export const analyzeFeatureGrants = ({
  grants,
  clientName,
  features,
  users,
  locations,
  studios,
}: AnalyzeFeatureGrantsInput): GrantAnalysis[] => {
  const byFeature = new Map<string, CleanupGrant[]>();
  for (const grant of grants) {
    const list = byFeature.get(grant.featureId);
    if (list) {
      list.push(grant);
    } else {
      byFeature.set(grant.featureId, [grant]);
    }
  }

  const analyses: GrantAnalysis[] = [];
  for (const [featureId, featureGrants] of byFeature) {
    analyses.push(
      ...analyzeFeature(featureGrants, features.get(featureId), {
        clientName,
        users,
        locations,
        studios,
      }),
    );
  }
  return analyses;
};

const targetLabelFor = (
  grant: CleanupGrant,
  {
    clientName,
    users,
    locations,
    studios,
  }: Omit<AnalyzeFeatureGrantsInput, 'grants' | 'features'>,
): string => {
  switch (grant.scopeType) {
    case FeatureGrantScopeType.User:
      return users.get(grant.scopeId)?.email ?? grant.scopeId;
    case FeatureGrantScopeType.Location:
      return locations.get(grant.scopeId)?.name ?? grant.scopeId;
    case FeatureGrantScopeType.Studio:
      return studios.get(grant.scopeId)?.name ?? grant.scopeId;
    case FeatureGrantScopeType.Client:
      return clientName;
    default:
      return grant.scopeId;
  }
};

const analyzeFeature = (
  featureGrants: CleanupGrant[],
  feature: CleanupFeature | undefined,
  context: Omit<AnalyzeFeatureGrantsInput, 'grants' | 'features'>,
): GrantAnalysis[] => {
  const needsReview = (grant: CleanupGrant, reason: string): GrantAnalysis => ({
    grant,
    targetLabel: targetLabelFor(grant, context),
    verdict: 'review',
    fallbackState: null,
    fallbackSource: null,
    fallbackGrantId: null,
    reason,
  });

  if (!feature) {
    return featureGrants.map((grant) =>
      needsReview(
        grant,
        'Feature not found in the catalog — nothing to compare against.',
      ),
    );
  }

  // An archived feature short-circuits to disabled before precedence is walked,
  // so its grants do nothing today — but would again if it were unarchived.
  if (feature.isArchived) {
    return featureGrants.map((grant) =>
      needsReview(
        grant,
        `Feature "${feature.key}" is archived, so no grant for it is evaluated.`,
      ),
    );
  }

  const { users, locations, studios } = context;
  const grantsByScope = new Map<
    FeatureGrantScopeType,
    Map<string, CleanupGrant>
  >(
    CHAIN.map((scopeType) => [
      scopeType,
      new Map(
        featureGrants
          .filter((grant) => grant.scopeType === scopeType)
          .map((grant) => [grant.scopeId, grant]),
      ),
    ]),
  );

  /**
   * Walks what's left of the chain below `from`, exactly as the engine does:
   * the first level with a grant for this member's scope id decides.
   */
  const walk = (from: number, targets: ScopeTargets): Fallback => {
    for (let level = from; level < CHAIN.length; level += 1) {
      const scopeType = CHAIN[level];
      const target = targets[scopeType];
      const atLevel = grantsByScope.get(scopeType) ?? new Map();
      if (!target || atLevel.size === 0) {
        continue;
      }
      // Country is the only per-member scope: members under one studio or
      // location can sit in different countries and nothing lists them. Still
      // decidable when every outcome agrees, since then the id can't matter.
      if (target === PER_MEMBER) {
        const below = walk(level + 1, targets);
        if (below.state === null) {
          return below;
        }
        const outcomes = new Set(
          [...atLevel.values()].map((grant) => grant.state),
        );
        outcomes.add(below.state);
        return outcomes.size === 1
          ? {
              state: below.state,
              source: `${below.source} and every ${scopeGrantSource(scopeType)}`,
            }
          : { state: null, source: scopeGrantSource(scopeType) };
      }
      const match = atLevel.get(target);
      if (match) {
        return {
          state: match.state,
          source: scopeGrantSource(scopeType),
          grantId: match.id,
        };
      }
    }
    return { state: feature.defaultState, source: FEATURE_DEFAULT };
  };

  /** The scope ids below a grant, or why it can't be judged at all. */
  const scopeTargets = (grant: CleanupGrant): ScopeTargets | Problem => {
    const client = { [FeatureGrantScopeType.Client]: grant.clientId };
    switch (grant.scopeType) {
      case FeatureGrantScopeType.Client:
      case FeatureGrantScopeType.Country:
        return client;

      case FeatureGrantScopeType.Studio: {
        const studio = studios.get(grant.scopeId);
        if (!studio) {
          return { unloadable: 'The studio' };
        }
        if (studio.clientId && studio.clientId !== grant.clientId) {
          return { inert: 'The studio belongs to a different client' };
        }
        return { ...client, [FeatureGrantScopeType.Country]: PER_MEMBER };
      }

      case FeatureGrantScopeType.Location: {
        const location = locations.get(grant.scopeId);
        if (!location) {
          return { unloadable: 'The location' };
        }
        // clientId is the studio's — a location reaches its client only through
        // one. No studio leaves it null and falls through, as the engine does.
        if (location.clientId && location.clientId !== grant.clientId) {
          return {
            inert: "The location's studio belongs to a different client",
          };
        }
        return {
          ...client,
          [FeatureGrantScopeType.Country]: PER_MEMBER,
          ...(location.studioId
            ? { [FeatureGrantScopeType.Studio]: location.studioId }
            : {}),
        };
      }

      case FeatureGrantScopeType.User: {
        const user = users.get(grant.scopeId);
        if (!user) {
          return { unloadable: 'The member' };
        }
        // The engine short-circuits before reading any grant, so these never
        // fire — not the same as having moved to another client.
        if (!user.clientId) {
          return { inert: 'The member has no client' };
        }
        if (user.clientId !== grant.clientId) {
          return { inert: 'The member belongs to a different client' };
        }
        return {
          ...client,
          ...(user.locationId
            ? { [FeatureGrantScopeType.Location]: user.locationId }
            : {}),
          ...(user.studioId
            ? { [FeatureGrantScopeType.Studio]: user.studioId }
            : {}),
          ...(user.countryCode
            ? { [FeatureGrantScopeType.Country]: user.countryCode }
            : {}),
        };
      }

      default:
        return { unloadable: 'The scope target' };
    }
  };

  return featureGrants.map((grant) => {
    const targets = scopeTargets(grant);
    if ('inert' in targets) {
      return needsReview(
        grant,
        `${targets.inert}, so this grant is never evaluated.`,
      );
    }
    if ('unloadable' in targets) {
      // A lookup failure and a deleted target look identical from here.
      return needsReview(
        grant,
        `${targets.unloadable} could not be loaded — it may have been deleted, or the lookup failed.`,
      );
    }

    const fallback = walk(CHAIN.indexOf(grant.scopeType) + 1, targets);
    if (fallback.state === null) {
      return {
        ...needsReview(
          grant,
          `Falls back to a ${fallback.source}, and the members under this scope can be in different countries — check it by hand.`,
        ),
        fallbackSource: fallback.source,
      };
    }

    const redundant = fallback.state === grant.state;
    return {
      grant,
      targetLabel: targetLabelFor(grant, context),
      verdict: redundant ? ('redundant' as const) : ('keep' as const),
      fallbackState: fallback.state,
      fallbackSource: fallback.source,
      fallbackGrantId: fallback.grantId ?? null,
      reason: redundant
        ? `Without it the feature still resolves to ${fallback.state}, from the ${fallback.source}.`
        : `Overrides the ${fallback.source} (${fallback.state}).`,
    };
  });
};
